import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api';

const MessageUnreadContext = createContext(null);

const REFRESH_INTERVAL_MS = 30000;

function countUnreadMessages(chats) {
  return chats.reduce((total, chat) => {
    const unread = Number(chat?.unread);

    return total + (
      Number.isFinite(unread)
        ? Math.max(0, Math.floor(unread))
        : 0
    );
  }, 0);
}

/*
 * Changing the identity key remounts the internal provider.
 * This clears the previous account's count immediately.
 */
export function MessageUnreadProvider({ user, children }) {
  const username = user?.username || '';

  const identityKey = JSON.stringify([
    user?._id || '',
    username,
  ]);

  return (
    <MessageUnreadState
      key={identityKey}
      username={username}
    >
      {children}
    </MessageUnreadState>
  );
}

function MessageUnreadState({ username, children }) {
  const { pathname } = useLocation();

  const [unreadDmCount, setUnreadDmCount] = useState(0);

  const activeRef = useRef(false);
  const requestVersionRef = useRef(0);
  const pendingRequestRef = useRef(null);

  // Prevent old or unmounted requests from updating state.
  useEffect(() => {
    activeRef.current = true;

    return () => {
      activeRef.current = false;
      requestVersionRef.current += 1;
      pendingRequestRef.current = null;
    };
  }, []);

  const refreshUnreadDmCount = useCallback(() => {
    if (!username || !activeRef.current) {
      return Promise.resolve(null);
    }

    // Reuse an existing request instead of fetching twice.
    if (pendingRequestRef.current) {
      return pendingRequestRef.current;
    }

    const requestVersion = ++requestVersionRef.current;

    const request = (async () => {
      try {
        const response = await api.get(
          `/messages/chats/${encodeURIComponent(username)}`
        );

        if (
          !activeRef.current ||
          requestVersion !== requestVersionRef.current
        ) {
          return null;
        }

        if (!Array.isArray(response.data)) {
          throw new Error('Unexpected chats response');
        }

        const total = countUnreadMessages(response.data);

        setUnreadDmCount(total);

        return total;
      } catch (error) {
        if (
          activeRef.current &&
          requestVersion === requestVersionRef.current
        ) {
          console.error(
            '❌ Failed to refresh unread messages:',
            error
          );
        }

        // Keep the last successful count if a request fails.
        return null;
      } finally {
        if (requestVersion === requestVersionRef.current) {
          pendingRequestRef.current = null;
        }
      }
    })();

    pendingRequestRef.current = request;

    return request;
  }, [username]);

  /*
   * Fetch on login and route changes.
   * Returning from a conversation therefore refreshes the badge.
   */
  useEffect(() => {
    refreshUnreadDmCount();
  }, [pathname, refreshUnreadDmCount]);

  useEffect(() => {
    if (!username) return;

    const refreshIfVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshUnreadDmCount();
      }
    };

    const interval = window.setInterval(
      refreshIfVisible,
      REFRESH_INTERVAL_MS
    );

    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener(
      'visibilitychange',
      refreshIfVisible
    );

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener(
        'visibilitychange',
        refreshIfVisible
      );
    };
  }, [username, refreshUnreadDmCount]);

  const value = useMemo(() => ({
    unreadDmCount,
    refreshUnreadDmCount,
  }), [unreadDmCount, refreshUnreadDmCount]);

  return (
    <MessageUnreadContext.Provider value={value}>
      {children}
    </MessageUnreadContext.Provider>
  );
}

export function useMessageUnreadContext() {
  const context = useContext(MessageUnreadContext);

  if (!context) {
    throw new Error(
      'useMessageUnreadContext must be used within a MessageUnreadProvider'
    );
  }

  return context;
}
